import cn from 'classnames';
import React, { memo } from 'react';

import { useIsMobile } from '@sravni/react-utils';
import type { ILocationV2 } from '@sravni/types/lib/locations';
import pluralize from '@sravni/utils/lib/pluralize';
import { replaceTemplate } from '@sravni/vitrina/lib/helpers/replaceHelper';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';

import { useBranches, useCountBranches } from '../../store/hooks/branches';
import { useProductConfig } from '../../store/hooks/productPopupInfo';
import Map from '../Map';

import style from './style.module.scss';

interface IProps {
  className?: string;
  headerClassName?: string;
  subHeaderClassName?: string;
  organization: any;
  region: ILocationV2;
  showHead?: boolean;
  product: IProduct;
  mapPlacement: string;
}

const BranchesMap: React.FC<IProps> = memo((props: IProps) => {
  const {
    className,
    headerClassName,
    subHeaderClassName,
    organization,
    region: { nameLocative, latitude, longitude },
    showHead = true,
  } = props;

  const config = useProductConfig();
  const {
    list: { desktop, mobile },
  } = config;
  const isMobile = useIsMobile();
  const branches = useBranches(organization.alias);
  const count = useCountBranches(organization.alias);
  const configMap = isMobile ? mobile : desktop;
  const {
    productInfo: {
      map: { heading },
    },
  } = configMap;

  const center = latitude && longitude ? [latitude, longitude] : undefined;

  const getHeading = (): string => {
    const { template, properties, defaultValue } = heading;

    return replaceTemplate(template, properties, organization, defaultValue);
  };

  const getBranchesCount = (): string => {
    if (!count) {
      return '';
    }

    const prefix = pluralize(count, 'отделение', 'отделения', 'отделений');

    return `${count} ${prefix}`;
  };

  const disableBehaviors = [];
  if (isMobile) {
    disableBehaviors.push('drag');
  }

  return (
    <div className={cn(style.container, className)}>
      <div>
        {showHead && (
          <div className={cn(headerClassName, style.wrapper)}>
            {getHeading()}&nbsp;
            {!config.default.isNoRegion && <span className={cn(style.region)}>{nameLocative}</span>}
          </div>
        )}
        <div className={cn(subHeaderClassName, style.subheader)}>{getBranchesCount()}</div>
      </div>
      <Map items={branches} center={center} disableBehaviors={disableBehaviors} />
    </div>
  );
});

export default BranchesMap;
